import type { WithId } from '@medplum/core';
import type { Observation } from '@medplum/fhirtypes';
import { ArrowLeftIcon, ChevronDownIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../utils/date';
import { formatVitalValue } from '../../utils/observation';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { Item, ItemActions, ItemContent } from '../ui/item';

export type SurveyDetailProps = {
  surveyName: string;
  /** Score Observations for one survey instrument, newest first. */
  results: WithId<Observation>[];
  /** Renders the per-question QuestionnaireResponse detail when a result row is expanded. */
  renderResponse: (result: WithId<Observation>) => React.ReactNode;
  /** When provided, renders a back button (used by the patient-web full-page detail view). */
  onBack?: () => void;
};

type ExpandableResultProps = {
  itemClassName?: string;
  result: WithId<Observation>;
  renderResponse: (result: WithId<Observation>) => React.ReactNode;
  testId: string;
  children: React.ReactNode;
};

const ExpandableResult = ({
  itemClassName,
  result,
  renderResponse,
  testId,
  children,
}: ExpandableResultProps): React.ReactElement => {
  const { t } = useTranslation('records');
  const resultDate = result.effectiveDateTime ? formatDate(result.effectiveDateTime) : t('unknown');

  return (
    <Collapsible>
      <CollapsibleTrigger
        className="group w-full text-left"
        data-testid={testId}
        aria-label={t('expandSurveyResult', { date: resultDate })}
      >
        <Item size="sm" className={itemClassName}>
          <ItemContent>{children}</ItemContent>
          <ItemActions>
            <ChevronDownIcon className="size-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
          </ItemActions>
        </Item>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="px-4 py-3">{renderResponse(result)}</div>
      </CollapsibleContent>
    </Collapsible>
  );
};

// oxlint-disable-next-line react/no-multi-comp
export const SurveyDetail = ({
  surveyName,
  results,
  renderResponse,
  onBack,
}: SurveyDetailProps): React.ReactElement => {
  const { t } = useTranslation('records');
  const effectiveTime = (obs: Observation): number =>
    obs.effectiveDateTime ? new Date(obs.effectiveDateTime).getTime() : 0;
  const sortedResults = [...results].sort((a, b) => effectiveTime(b) - effectiveTime(a));
  const latest = sortedResults[0];
  const resultCount = sortedResults.length;

  const backButton = onBack ? (
    <Button variant="ghost" onClick={onBack} className="mb-4 flex items-center gap-2" data-testid="back-to-surveys">
      <ArrowLeftIcon className="size-4" />
      <span>{t('backToSurveys')}</span>
    </Button>
  ) : null;

  if (!latest) {
    return (
      <div className="mx-auto w-full max-w-4xl p-5">
        {backButton}
        <p className="py-8 text-center text-sm text-muted-foreground">{t('noSurveysFound')}</p>
      </div>
    );
  }

  const formatValue = (obs: Observation): string => formatVitalValue(obs, t('unknown'));

  return (
    <div className="mx-auto w-full max-w-4xl p-5">
      {backButton}
      <div className="mb-3">
        <h1 className="text-sm font-medium uppercase">{surveyName}</h1>
        <p className="text-xs text-muted-foreground">{t('resultCountOnRecord', { count: resultCount })}</p>
      </div>

      <div className="mb-3">
        <ExpandableResult
          result={latest}
          renderResponse={renderResponse}
          itemClassName="bg-muted/50"
          testId="survey-latest-result"
        >
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-xs">
              {t('latestResult')}
            </Badge>
            <span className="text-xs text-muted-foreground">
              {t('mostRecentLabel')} {latest.effectiveDateTime ? formatDate(latest.effectiveDateTime) : t('unknown')}
            </span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-sm font-semibold">{formatValue(latest)}</span>
          </div>
          <div className="grid grid-cols-2 gap-x-4 text-xs">
            <div>
              <span className="text-muted-foreground">{t('status')}</span> {latest.status ?? t('unknown')}
            </div>
            {latest.effectiveDateTime && (
              <div>
                <span className="text-muted-foreground">{t('collected')}</span> {formatDate(latest.effectiveDateTime)}
              </div>
            )}
          </div>
        </ExpandableResult>
      </div>

      {sortedResults.length > 1 && (
        <div>
          <h2 className="mb-2 text-xs font-semibold text-muted-foreground">{t('resultHistory')}</h2>
          <div className="space-y-1">
            {sortedResults.slice(1).map((result, index) => (
              <ExpandableResult
                key={result.id ?? index}
                result={result}
                renderResponse={renderResponse}
                testId="survey-result-row"
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-xs text-muted-foreground">
                    {result.effectiveDateTime ? formatDate(result.effectiveDateTime) : t('unknown')}
                  </span>
                  <span className="text-sm font-semibold">{formatValue(result)}</span>
                </div>
              </ExpandableResult>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
